import { isClickedOutside, normalizeString } from "@theme/utilities";

/**
 * @typedef {Object} ComboboxRefs
 * @property {HTMLInputElement} valueInput
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} triggerLabel
 * @property {HTMLElement} triggerIcon
 * @property {HTMLElement} popover
 * @property {HTMLInputElement} search
 * @property {HTMLUListElement} list
 * @property {HTMLElement} empty
 */

/** @typedef {{ label: string, iso: string }} CountryRecord */

const SHOPIFY_FLAG_BASE = "https://cdn.shopify.com/static/images/flags";

class ThemeCombobox extends HTMLElement {
  /** @type {string} */
  #placeholder = "Select…";

  /** @type {HTMLElement | null} */
  #highlightedOption = null;

  /** @type {boolean} */
  #countriesReady = false;

  async connectedCallback() {
    const refs = this.#getRefs();
    if (!refs) return;

    this.#placeholder =
      refs.triggerLabel.textContent?.trim() || this.#placeholder;

    const error = this.querySelector("[data-combobox-error]");
    if (error instanceof HTMLElement) {
      error.dataset.defaultError = error.textContent.trim();
    }

    const countriesUrl = this.dataset.countriesUrl;
    if (countriesUrl) {
      await this.#loadCountries(refs, countriesUrl);
    }

    refs.trigger.addEventListener("click", this.#handleTriggerClick);
    refs.search.addEventListener("input", this.#handleSearchInput);
    refs.search.addEventListener("keydown", this.#handleSearchKeyDown);
    refs.list.addEventListener("click", this.#handleListClick);

    this.#bindOptions(refs);
    document.addEventListener("click", this.#handleDocumentClick);
    document.addEventListener("keydown", this.#handleDocumentKeyDown);
    this.#countriesReady = true;
  }

  disconnectedCallback() {
    const refs = this.#getRefs();
    refs?.trigger.removeEventListener("click", this.#handleTriggerClick);
    refs?.search.removeEventListener("input", this.#handleSearchInput);
    refs?.search.removeEventListener("keydown", this.#handleSearchKeyDown);
    refs?.list.removeEventListener("click", this.#handleListClick);
    document.removeEventListener("click", this.#handleDocumentClick);
    document.removeEventListener("keydown", this.#handleDocumentKeyDown);
  }

  /** @returns {ComboboxRefs | null} */
  #getRefs() {
    const valueInput = this.querySelector("[data-combobox-value]");
    const trigger = this.querySelector("[data-combobox-trigger]");
    const triggerLabel = this.querySelector("[data-combobox-trigger-label]");
    const triggerIcon = this.querySelector("[data-combobox-trigger-icon]");
    const popover = this.querySelector("[data-combobox-popover]");
    const search = this.querySelector("[data-combobox-search]");
    const list = this.querySelector("[data-combobox-list]");
    const empty = this.querySelector("[data-combobox-empty]");

    if (
      !(valueInput instanceof HTMLInputElement) ||
      !(trigger instanceof HTMLButtonElement) ||
      !(triggerLabel instanceof HTMLElement) ||
      !(triggerIcon instanceof HTMLElement) ||
      !(popover instanceof HTMLElement) ||
      !(search instanceof HTMLInputElement) ||
      !(list instanceof HTMLUListElement) ||
      !(empty instanceof HTMLElement)
    ) {
      return null;
    }

    return {
      valueInput,
      trigger,
      triggerLabel,
      triggerIcon,
      popover,
      search,
      list,
      empty,
    };
  }

  /**
   * @param {ComboboxRefs} refs
   * @param {string} countriesUrl
   */
  async #loadCountries(refs, countriesUrl) {
    this.dataset.loading = "true";
    refs.trigger.disabled = true;

    try {
      /** @type {CountryRecord[]} */
      let isoRecords = [];
      const response = await fetch(countriesUrl);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) isoRecords = data;
      }

      const useJsonOnly = this.dataset.countriesFromJson === "true";
      const labels = useJsonOnly ? [] : this.#getCountryNameLabels();
      const items =
        labels.length > 0
          ? labels.map((label) => ({
              label,
              iso: this.#resolveIso(label, isoRecords),
            }))
          : isoRecords.filter((entry) => entry.label && entry.iso);

      refs.list.replaceChildren();
      const fragment = document.createDocumentFragment();

      for (const [index, item] of items.entries()) {
        if (!item.label) continue;
        const iso = item.iso ?? "";
        const value = iso ? `${item.label} [${iso}]` : item.label;
        const icon = iso
          ? `${SHOPIFY_FLAG_BASE}/${iso.toLowerCase()}.svg`
          : "";

        const option = document.createElement("li");
        option.className = "combobox__option";
        option.setAttribute("role", "option");
        option.setAttribute("tabindex", "-1");
        option.dataset.comboboxOption = "";
        option.dataset.value = value;
        option.dataset.label = item.label;
        if (icon) option.dataset.icon = icon;
        option.id = `${this.id}-option-${index}`;
        option.setAttribute("aria-selected", "false");

        const iconWrap = document.createElement("span");
        iconWrap.className = "combobox__option-icon";
        iconWrap.setAttribute("aria-hidden", "true");
        if (icon) {
          const img = document.createElement("img");
          img.className = "combobox__option-icon-image";
          img.src = icon;
          img.alt = "";
          img.width = 20;
          img.height = 15;
          img.loading = "lazy";
          img.addEventListener("error", () => {
            img.remove();
          });
          iconWrap.append(img);
        }

        const labelEl = document.createElement("span");
        labelEl.className = "combobox__option-label";
        labelEl.textContent = item.label;

        const check = document.createElement("span");
        check.className = "combobox__option-check svg-wrapper";
        check.setAttribute("aria-hidden", "true");
        const checkTemplate = this.querySelector(
          "[data-combobox-check-template]",
        );
        if (checkTemplate instanceof HTMLTemplateElement) {
          check.append(checkTemplate.content.cloneNode(true));
        }

        option.append(iconWrap, labelEl, check);
        fragment.append(option);
      }

      refs.list.append(fragment);
      refs.empty.hidden = items.length > 0;
    } catch (error) {
      console.error("Failed to load combobox countries", error);
    } finally {
      delete this.dataset.loading;
      refs.trigger.disabled = false;
    }
  }

  /** @returns {string[]} */
  #getCountryNameLabels() {
    const selector = this.dataset.countryNamesSelect;
    if (!selector) return [];

    const select = document.querySelector(selector);
    if (!(select instanceof HTMLSelectElement)) return [];

    return Array.from(select.options)
      .map((option) => option.textContent?.trim() ?? "")
      .filter((label) => label && label !== "---");
  }

  /**
   * @param {string} label
   * @param {CountryRecord[]} isoRecords
   * @returns {string}
   */
  #resolveIso(label, isoRecords) {
    const normalized = normalizeString(label);
    const exact = isoRecords.find(
      (entry) => normalizeString(entry.label) === normalized,
    );
    if (exact?.iso) return exact.iso;

    const byIso = isoRecords.find(
      (entry) => normalizeString(entry.iso) === normalized,
    );
    return byIso?.iso ?? "";
  }

  /**
   * @param {string} icon
   */
  #syncFlagPropertyValue(icon) {
    const flagInput = this.querySelector("[data-combobox-flag-value]");
    if (!(flagInput instanceof HTMLInputElement)) return;

    const flagUrl = icon || "";

    if (flagInput.value === flagUrl) return;

    flagInput.value = flagUrl;
    flagInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /** @param {ComboboxRefs} refs */
  #bindOptions(refs) {
    this.#getVisibleOptions(refs).forEach((option) => {
      option.addEventListener("mousedown", (event) => {
        event.preventDefault();
      });
    });
  }

  /** @param {ComboboxRefs} refs */
  #getOptions(refs) {
    return Array.from(
      refs.list.querySelectorAll("[data-combobox-option]"),
    ).filter((node) => node instanceof HTMLElement);
  }

  /** @param {ComboboxRefs} refs */
  #getVisibleOptions(refs) {
    return this.#getOptions(refs).filter(
      (option) => !option.hasAttribute("hidden"),
    );
  }

  open() {
    const refs = this.#getRefs();
    if (!refs) return;

    this.dataset.open = "true";
    refs.trigger.setAttribute("aria-expanded", "true");
    refs.popover.hidden = false;
    refs.search.value = "";
    this.#filterOptions(refs, "");
    this.#setHighlightedOption(refs, this.#getSelectedOption(refs));
    refs.search.focus();
  }

  close() {
    const refs = this.#getRefs();
    if (!refs) return;

    delete this.dataset.open;
    refs.trigger.setAttribute("aria-expanded", "false");
    refs.popover.hidden = true;
    refs.search.value = "";
    this.#filterOptions(refs, "");
    this.#setHighlightedOption(refs, null);
  }

  /** @param {ComboboxRefs} refs */
  #getSelectedOption(refs) {
    const value = refs.valueInput.value;
    if (!value) return null;

    return (
      this.#getOptions(refs).find((option) => option.dataset.value === value) ??
      null
    );
  }

  /**
   * @param {ComboboxRefs} refs
   * @param {string} query
   */
  #filterOptions(refs, query) {
    const normalizedQuery = normalizeString(query);
    let visibleCount = 0;

    for (const option of this.#getOptions(refs)) {
      const label = normalizeString(option.dataset.label ?? "");
      const matches = normalizedQuery === "" || label.includes(normalizedQuery);
      option.hidden = !matches;
      if (matches) visibleCount += 1;
    }

    refs.empty.hidden = visibleCount > 0;
  }

  /**
   * @param {ComboboxRefs} refs
   * @param {HTMLElement | null} option
   */
  #setHighlightedOption(refs, option) {
    for (const item of this.#getOptions(refs)) {
      item.toggleAttribute("data-highlighted", item === option);
    }
    this.#highlightedOption = option;

    if (option) {
      refs.search.setAttribute("aria-activedescendant", option.id);
      option.scrollIntoView({ block: "nearest" });
    } else {
      refs.search.removeAttribute("aria-activedescendant");
    }
  }

  /**
   * @param {ComboboxRefs} refs
   * @param {HTMLElement} option
   */
  #selectOption(refs, option) {
    const value = option.dataset.value ?? "";
    const label = option.dataset.label ?? "";
    const icon = option.dataset.icon ?? "";

    refs.valueInput.value = value;
    refs.valueInput.dispatchEvent(new Event("change", { bubbles: true }));
    this.#syncFlagPropertyValue(icon);
    this.removeAttribute("combobox-input-error");
    refs.trigger.removeAttribute("aria-invalid");

    const error = this.querySelector("[data-combobox-error]");
    if (error instanceof HTMLElement) {
      error.hidden = true;
    }

    refs.triggerLabel.textContent = label;
    refs.triggerLabel.classList.remove("is-placeholder");

    if (icon) {
      refs.triggerIcon.hidden = false;
      refs.triggerIcon.style.backgroundImage = `url("${icon}")`;
      refs.triggerIcon.replaceChildren();
    } else {
      refs.triggerIcon.hidden = true;
      refs.triggerIcon.style.backgroundImage = "";
    }

    for (const item of this.#getOptions(refs)) {
      const selected = item === option;
      item.setAttribute("aria-selected", selected ? "true" : "false");
    }

    this.close();
    refs.trigger.focus();
  }

  reset() {
    const refs = this.#getRefs();
    if (!refs) return;

    refs.valueInput.value = refs.valueInput.defaultValue ?? "";
    refs.valueInput.setCustomValidity?.("");

    const flagInput = this.querySelector("[data-combobox-flag-value]");
    if (flagInput instanceof HTMLInputElement) {
      flagInput.value = flagInput.defaultValue ?? "";
    }

    refs.triggerLabel.textContent = this.#placeholder;
    refs.triggerLabel.classList.add("is-placeholder");
    refs.triggerIcon.hidden = true;
    refs.triggerIcon.style.backgroundImage = "";
    refs.triggerIcon.replaceChildren();

    this.removeAttribute("combobox-input-error");
    refs.trigger.removeAttribute("aria-invalid");
    const error = this.querySelector("[data-combobox-error]");
    if (error instanceof HTMLElement) {
      error.hidden = true;
    }

    for (const item of this.#getOptions(refs)) {
      item.setAttribute("aria-selected", "false");
    }
    this.#highlightedOption = null;
  }

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    event.preventDefault();
    if (this.dataset.loading === "true") return;
    if (this.dataset.open === "true") {
      this.close();
    } else {
      this.open();
    }
  };

  /** @param {Event} event */
  #handleSearchInput = (event) => {
    const refs = this.#getRefs();
    if (!refs || !(event.target instanceof HTMLInputElement)) return;

    this.#filterOptions(refs, event.target.value);
    const visible = this.#getVisibleOptions(refs);
    this.#setHighlightedOption(refs, visible[0] ?? null);
  };

  /** @param {KeyboardEvent} event */
  #handleSearchKeyDown = (event) => {
    const refs = this.#getRefs();
    if (!refs) return;

    const visible = this.#getVisibleOptions(refs);

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (visible.length === 0) return;
        const firstVisible = visible[0];
        if (!firstVisible) return;
        const currentIndex = visible.indexOf(
          this.#highlightedOption ?? firstVisible,
        );
        const next = visible[(currentIndex + 1) % visible.length];
        if (next) this.#setHighlightedOption(refs, next);
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (visible.length === 0) return;
        const firstVisible = visible[0];
        if (!firstVisible) return;
        const currentIndex = visible.indexOf(
          this.#highlightedOption ?? firstVisible,
        );
        const prev =
          visible[(currentIndex - 1 + visible.length) % visible.length];
        if (prev) this.#setHighlightedOption(refs, prev);
        break;
      }
      case "Enter": {
        event.preventDefault();
        if (this.#highlightedOption instanceof HTMLElement) {
          this.#selectOption(refs, this.#highlightedOption);
        }
        break;
      }
      case "Escape": {
        event.preventDefault();
        this.close();
        refs.trigger.focus();
        break;
      }
      case "Tab":
        this.close();
        break;
    }
  };

  /** @param {MouseEvent} event */
  #handleListClick = (event) => {
    const refs = this.#getRefs();
    if (!refs || !(event.target instanceof Element)) return;

    const option = event.target.closest("[data-combobox-option]");
    if (!(option instanceof HTMLElement) || option.hidden) return;

    this.#selectOption(refs, option);
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (this.dataset.open !== "true") return;
    if (isClickedOutside(event, this)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleDocumentKeyDown = (event) => {
    if (event.key === "Escape" && this.dataset.open === "true") {
      event.preventDefault();
      this.close();
      this.#getRefs()?.trigger.focus();
    }
  };
}

if (!customElements.get("theme-combobox")) {
  customElements.define("theme-combobox", ThemeCombobox);
}
